import { isDemoDatasource, ownDatasources, type DemoLike } from "./demo-preference";

/**
 * MOSA-694: verify-email joined this list. Its confirmation renders after the
 * verify signs the user in, so without it the signed-in chrome appeared on top
 * of a door-step screen.
 */
const isAuthRoute = (pathname: string): boolean =>
  pathname === "/auth" || pathname === "/auth/verify-email";

/**
 * When the authenticated chrome (Nav + mobile header) should show.
 * First-run stays focused: no sidebar on the wizard, auth, boot routes, or
 * when the user has no own source — unless they are already on a demo route.
 *
 * Only the first load of the source list hides the sidebar for loading. A
 * refetch keeps the last decision so the rail does not flicker.
 */
export function shouldShowAppSidebar(args: {
  isAuthenticated: boolean;
  pathname: string;
  datasources: DemoLike[] | undefined;
  /** True only while the first source list request has not settled. */
  isInitialLoad: boolean;
}): boolean {
  const { isAuthenticated, pathname, datasources, isInitialLoad } = args;
  if (!isAuthenticated) return false;

  const isBootRoute = pathname === "/guest" || pathname === "/setup";
  const isWizardRoute = pathname === "/connect/sheets";
  if (isBootRoute || isAuthRoute(pathname) || isWizardRoute) {
    return false;
  }
  if (isInitialLoad) {
    return false;
  }

  const pathParts = pathname.split("/").filter(Boolean);
  const routeDatasourceId =
    pathParts[0] === "datasources" && pathParts[1] ? pathParts[1] : null;
  const onDemoRoute =
    !!routeDatasourceId &&
    isDemoDatasource(
      (datasources ?? []).find((datasource) => datasource.id === routeDatasourceId),
    );
  const hasOwnSources = ownDatasources(datasources).length > 0;
  return hasOwnSources || onDemoRoute;
}

/**
 * Compact top bar when the sidebar is hidden but the user is signed in.
 * Hide both chromes while the first source list is still loading so existing
 * users never flash the first-run empty chrome.
 */
export function shouldShowCompactChrome(args: {
  isAuthenticated: boolean;
  pathname: string;
  showSidebar: boolean;
  isInitialLoad?: boolean;
}): boolean {
  const { isAuthenticated, pathname, showSidebar, isInitialLoad } = args;
  if (!isAuthenticated || showSidebar) return false;
  if (isInitialLoad) return false;
  const isBootRoute = pathname === "/guest" || pathname === "/setup";
  return !isBootRoute && !isAuthRoute(pathname);
}
